/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ApiKey } from '../models/ApiKey';
import type { ApiKeyList } from '../models/ApiKeyList';
import type { CreateApiKeyInput } from '../models/CreateApiKeyInput';
import type { CreateUserInput } from '../models/CreateUserInput';
import type { User } from '../models/User';
import type { UsersList } from '../models/UsersList';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class AdminService {

  constructor(public readonly httpRequest: BaseHttpRequest) {}

  /**
   * Search for users
   * @returns UsersList Successful response
   * @throws ApiError
   */
  public findUsers(): CancelablePromise<UsersList> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/users',
      errors: {
        401: `The request was not authorized. Most likely you forgot to send the API key in the header \`Authorization\`.`,
        403: `You are not allowed to perform the requested operation on this resource.`,
      },
    });
  }

  /**
   * Create or update a user
   * @param userId The ID of the user to create
   * @param requestBody
   * @returns User Successful response
   * @throws ApiError
   */
  public createUser(
    userId: string,
    requestBody: CreateUserInput,
  ): CancelablePromise<User> {
    return this.httpRequest.request({
      method: 'PUT',
      url: '/users/{userId}',
      path: {
        'userId': userId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        401: `The request was not authorized. Most likely you forgot to send the API key in the header \`Authorization\`.`,
        403: `You are not allowed to perform the requested operation on this resource.`,
      },
    });
  }

  /**
   * Delete a user
   * @param userId The ID of the user to create
   * @returns void
   * @throws ApiError
   */
  public deleteUser(
    userId: string,
  ): CancelablePromise<void> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/users/{userId}',
      path: {
        'userId': userId,
      },
      errors: {
        401: `The request was not authorized. Most likely you forgot to send the API key in the header \`Authorization\`.`,
        403: `You are not allowed to perform the requested operation on this resource.`,
        404: `The object was not found.`,
      },
    });
  }

  /**
   * Get all API keys of a user
   * @param userId The ID of the user
   * @returns ApiKeyList Successful response
   * @throws ApiError
   */
  public getUserApiKeys(
    userId: string,
  ): CancelablePromise<ApiKeyList> {
    return this.httpRequest.request({
      method: 'GET',
      url: '/users/{userId}/api-keys',
      path: {
        'userId': userId,
      },
      errors: {
        401: `The request was not authorized. Most likely you forgot to send the API key in the header \`Authorization\`.`,
        403: `You are not allowed to perform the requested operation on this resource.`,
      },
    });
  }

  /**
   * Create a new API key for a user
   * @param userId The ID of the user to create
   * @param requestBody
   * @returns ApiKey Successful response
   * @throws ApiError
   */
  public createUserApiKey(
    userId: string,
    requestBody: CreateApiKeyInput,
  ): CancelablePromise<ApiKey> {
    return this.httpRequest.request({
      method: 'POST',
      url: '/users/{userId}/api-keys',
      path: {
        'userId': userId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        401: `The request was not authorized. Most likely you forgot to send the API key in the header \`Authorization\`.`,
        403: `You are not allowed to perform the requested operation on this resource.`,
        404: `The object was not found.`,
      },
    });
  }

  /**
   * Delete an API key of a user
   * @param userId The ID of the user
   * @param apiKeyId The ID of the API key
   * @returns void
   * @throws ApiError
   */
  public deleteUserApiKey(
    userId: string,
    apiKeyId: string,
  ): CancelablePromise<void> {
    return this.httpRequest.request({
      method: 'DELETE',
      url: '/users/{userId}/api-keys/{apiKeyId}',
      path: {
        'userId': userId,
        'apiKeyId': apiKeyId,
      },
      errors: {
        401: `The request was not authorized. Most likely you forgot to send the API key in the header \`Authorization\`.`,
        403: `You are not allowed to perform the requested operation on this resource.`,
        404: `The object was not found.`,
      },
    });
  }

}
